import React, { useState, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getColors } from '../services/api';
import { OptimizedImage } from './OptimizedImage';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SegmentSection = () => {
  const [colors, setColors] = useState([]);
  const [_segments, setSegments] = useState([]);
  const [segmentContent, setSegmentContent] = useState(null);
  const [colorsSectionContent, setColorsSectionContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const colorsData = await getColors();
        setColors(colorsData);
        
        // Fetch segment content
        const segmentResponse = await fetch(`${BACKEND_URL}/api/segment/content`);
        if (segmentResponse.ok) {
          const segmentData = await segmentResponse.json();
          setSegmentContent(segmentData);
          setSegments(segmentData.segments || []);
        }
        
        // Fetch colors section CMS content
        const colorsSectionResponse = await fetch(`${BACKEND_URL}/api/cms/colors-section/public`);
        if (colorsSectionResponse.ok) {
          const colorsSectionData = await colorsSectionResponse.json();
          setColorsSectionContent(colorsSectionData);
        }
      } catch (err) {
        logger.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Wheel event for horizontal scroll with mouse wheel
  const handleWheel = (e) => {
    if (!scrollContainerRef.current) return;
    // Allow horizontal scrolling with vertical wheel
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  const getRepeatedColors = () => {
    if (colors.length === 0) return [];
    const targetCount = 50;
    const repeated = [];
    let index = 0;
    while (repeated.length < targetCount) {
      repeated.push({
        ...colors[index % colors.length],
        id: `${colors[index % colors.length].id}-${Math.floor(index / colors.length)}`
      });
      index++;
    }
    return repeated;
  };

  const displayColors = getRepeatedColors();

  // Default tabs for backwards compatibility
  const defaultTabs = [
    {
      title: "Horeca & Hospitality",
      heading: "Voor Hotels, Restaurants en Boutiqueconcepten",
      description: [
        "In de hospitalitywereld draait alles om beleving. De handgemaakte manden van L'ART brengen rust en verfijning in kamers, lobby's en restaurants. Ze voegen niet enkel stijl toe, maar versterken de identiteit van jouw merk of locatie.",
        "Personaliseer kleuren of voeg een subtiele gravure toe met je logo — details die het verschil maken."
      ],
      image: "https://www.dropbox.com/scl/fo/h4b8ovqgymjrdojt8aefi/AHk5GQdifnYRcL8KSbG_0YI/Reportage%20OKT25/L%27ART/2.jpg?rlkey=qflewhauikr3vqodbe6lqir3g&dl=1",
      primaryButton: "Contacteer Leen"
    },
    {
      title: "Winkels & Merken",
      heading: "Voor Winkels en Merken die Authenticiteit Willen Uitstralen",
      description: [
        "Onze manden zijn meer dan producten: ze dragen een verhaal van Belgisch vakmanschap. Winkels kunnen ze opnemen als selectief, ambachtelijk accent in hun aanbod, of samen met L'ART een samenwerking opzetten op maat van hun merk.",
        "Elke collectie kan aangepast worden in kleur en uitvoering, zodat ze aansluit bij jouw identiteit."
      ],
      image: "https://www.dropbox.com/scl/fo/h4b8ovqgymjrdojt8aefi/AHk5GQdifnYRcL8KSbG_0YI/Reportage%20OKT25/L%27ART/7.jpg?rlkey=qflewhauikr3vqodbe6lqir3g&dl=1",
      primaryButton: "Samenwerking Aanvragen"
    },
    {
      title: "Interieurprofessionals",
      heading: "Voor Interieurprofessionals met Oog voor Afwerking en Detail",
      description: [
        "Onze manden brengen warmte, textuur en harmonie in elk interieurproject. Of je nu interieurarchitect, stylist of vakman bent, L'ART biedt de mogelijkheid om projecten tot in de puntjes af te werken met handgemaakte accenten.",
        "Elk detail telt zodat de ruimte één geheel vormt en haar eigen verhaal vertelt."
      ],
      image: "https://www.dropbox.com/scl/fo/h4b8ovqgymjrdojt8aefi/AHk5GQdifnYRcL8KSbG_0YI/Reportage%20OKT25/L%27ART/5.jpg?rlkey=qflewhauikr3vqodbe6lqir3g&dl=1",
      primaryButton: "Ontdek Mogelijkheden"
    }
  ];

  // Use API data if available, fallback to defaults
  const professionalTabs = (segmentContent?.tabs && segmentContent.tabs.length > 0) ? segmentContent.tabs : defaultTabs;

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="professionals" className="py-20 bg-gradient-to-br from-terra-beige/5 via-white to-terra-beige/5 scroll-mt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-sweet-sans text-3xl sm:text-4xl font-normal text-earth-brown mb-6 uppercase tracking-wide">
            {segmentContent?.main_title || "Professionals die kiezen voor L'ART"}
          </h2>
          <p className="font-sweet-sans text-sm text-almost-black/80 max-w-4xl mx-auto leading-relaxed">
            {segmentContent?.main_description || "Of u nu een winkel runt, een horecaconcept beheert of als interieurprofessional werkt — onze handgemaakte manden brengen authenticiteit en vakmanschap naar uw project."}
          </p>
        </div>
        
        {/* Professional Sections - Stacked with Alternating Layout */}
        <div className="mb-16 space-y-20">
          {professionalTabs.map((tab, index) => (
            <div
              key={index}
              className={`grid lg:grid-cols-2 gap-16 items-center ${
                index === 1 ? 'lg:grid-flow-dense' : ''
              }`}
            >
              {/* Image - order changes for middle item */}
              <div className={`overflow-hidden shadow-2xl ${
                index === 1 ? 'lg:col-start-2' : ''
              }`}>
                <img
                  src={tab.image?.startsWith('http') ? tab.image : `${BACKEND_URL}${tab.image}`}
                  alt={tab.title}
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                  onError={(e) => {
                    // Fallback to placeholder if image fails
                    e.target.src = `${BACKEND_URL}/uploads/samenwerken-photo.jpg`;
                  }}
                />
              </div>

              {/* Content */}
              <div className={index === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                <h3 className="font-sweet-sans text-xl sm:text-2xl font-normal text-earth-brown mb-6 leading-tight uppercase tracking-wide">
                  {tab.heading}
                </h3>
                {tab.description.map((paragraph, pIndex) => (
                  <p key={pIndex} className="font-sweet-sans text-sm text-almost-black/70 leading-relaxed mb-5">
                    {paragraph}
                  </p>
                ))}
                <div className="flex gap-6 mt-8">
                  <button
                    onClick={scrollToContact}
                    className="text-earth-brown font-sweet-sans text-sm uppercase tracking-wider underline hover:text-earth-brown/70 transition-colors duration-200 bg-transparent border-0 cursor-pointer p-0 font-bold"
                  >
                    {tab.primaryButton}
                  </button>
                  <a
                    href="/b2b/login"
                    className="text-earth-brown font-sweet-sans text-sm uppercase tracking-wider underline hover:text-earth-brown/70 transition-colors duration-200 font-bold"
                  >
                    B2B Portal Login
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Samenwerken Section - Reversed layout (text left, image right) */}
        <div className="mb-16 pt-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Content - First on mobile, first column on desktop */}
            <div className="flex flex-col justify-center order-2 lg:order-1">
              <h3 className="font-sweet-sans text-2xl sm:text-3xl font-normal text-earth-brown mb-6 uppercase tracking-wide">
                {segmentContent?.samenwerken_title || "SAMENWERKEN?"}
              </h3>
              <p className="font-sweet-sans text-sm text-almost-black/80 leading-relaxed mb-8">
                {segmentContent?.samenwerken_description || "Vertel ons over jouw project of concept. We bekijken graag samen hoe L'ART er een unieke toets aan kan geven."}
              </p>
              
              {/* Contact Info */}
              <div className="space-y-2 mb-6">
                <a 
                  href={`mailto:${segmentContent?.samenwerken_email || "info@l-art.be"}`}
                  className="flex items-center gap-2 text-earth-brown hover:text-terra-beige transition-colors font-sweet-sans text-sm"
                >
                  <span>✉</span>
                  <span>{segmentContent?.samenwerken_email || "info@l-art.be"}</span>
                </a>
                <a 
                  href={`tel:${(segmentContent?.samenwerken_phone || "+32 471 98 34 04").replace(/\s/g, '')}`}
                  className="flex items-center gap-2 text-earth-brown hover:text-terra-beige transition-colors font-sweet-sans text-sm"
                >
                  <span>☎</span>
                  <span>{segmentContent?.samenwerken_phone || "+32 471 98 34 04"}</span>
                </a>
              </div>
              
              {/* Signature */}
              <div className="mt-4">
                {segmentContent?.samenwerken_signature && (
                  <img
                    src={getImageUrl(segmentContent.samenwerken_signature)}
                    alt={segmentContent?.samenwerken_name || "Leen Vermeir"}
                    className="h-12 w-auto mb-2"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <p className="text-4xl text-earth-brown" style={{ fontFamily: "'Jalliya', cursive" }}>
                  {segmentContent?.samenwerken_name || "Leen Vermeir"}
                </p>
              </div>
            </div>
            
            {/* Image - Second on mobile, second column on desktop */}
            <div className="aspect-[4/3] overflow-hidden shadow-2xl order-1 lg:order-2">
              <img
                src={getImageUrl(segmentContent?.samenwerken_photo || "/atelier-leen.jpg")}
                alt="Samenwerken met L'ART"
                className="w-full h-full object-cover object-top"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          </div>
        </div>

        {/* Colors Section */}
        <div id="kleuren" className="mb-16 pt-16">
          {/* Quote */}
          <div className="w-full mb-32">
            <blockquote className="bg-gradient-to-br from-terra-beige/10 via-earth-brown/5 to-terra-beige/10 border-2 border-terra-beige/20 p-6 sm:p-8 text-center">
              <p className="font-sweet-sans text-sm sm:text-base text-earth-brown leading-relaxed italic">
                "{segmentContent?.quote_text || "Met zorg geproduceerd in België, brengen onze producten design, functionaliteit en een premium klantervaring samen. Met een kleurengamma en flexibele opties in vorm en grootte, is L'Art de perfecte mix van vakmanschap en maatwerk."}"
              </p>
            </blockquote>
          </div>
          
          <div className="text-center mb-16">
            <h3 className="font-sweet-sans text-3xl sm:text-4xl font-normal text-earth-brown mb-6 uppercase tracking-wide">
              {colorsSectionContent?.title || segmentContent?.colors_title || "Kleurafstemming op uw Merkidentiteit"}
            </h3>
            <p className="font-sweet-sans text-sm text-almost-black/80 max-w-3xl mx-auto leading-relaxed">
              {colorsSectionContent?.description || segmentContent?.colors_description || "Van natuurlijke tinten tot gedurfde kleuren — perfecte match met uw huisstijl en interieur."}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-terra-beige animate-spin" />
            </div>
          ) : (
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-terra-beige/5 to-earth-brown/5  -z-10"></div>
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-terra-beige/10 rounded-none blur-3xl -z-10"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-earth-brown/5 rounded-none blur-3xl -z-10"></div>
              
              <div className="bg-earth-brown/90 text-white px-6 py-3 rounded-t-2xl flex items-center justify-center gap-3">
                <ArrowRight className="w-5 h-5" />
                <span className="font-sweet-sans text-sm uppercase tracking-wide">
                  {colorsSectionContent?.slider_header_text || "Sleep horizontaal voor meer kleuren"}
                </span>
                <ArrowRight className="w-5 h-5" />
              </div>
              
              <div className="relative">
                {/* Scroll navigation buttons */}
                <button 
                  onClick={() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
                    }
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 hover:opacity-100 transition-opacity duration-300 group-hover:opacity-70"
                  aria-label="Scroll left"
                >
                  <ArrowRight className="w-5 h-5 text-earth-brown rotate-180" />
                </button>
                <button 
                  onClick={() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-70 hover:opacity-100 transition-opacity duration-300"
                  aria-label="Scroll right"
                >
                  <ArrowRight className="w-5 h-5 text-earth-brown" />
                </button>
                
                <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white via-white/50 to-transparent z-10 pointer-events-none rounded-bl-2xl"></div>
                <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white/50 to-transparent z-10 pointer-events-none rounded-br-2xl"></div>
                
                <div 
                  ref={scrollContainerRef}
                  className={`overflow-x-auto py-8 px-8 scrollbar-visible select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#8B6F47 #F5F1E8',
                    WebkitOverflowScrolling: 'touch',  // Smooth scrolling on iOS
                    scrollBehavior: 'smooth'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onWheel={handleWheel}
                >
                  <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                    {displayColors.map((color, index) => (
                      <div 
                        key={color.id} 
                        className="group transform transition-all duration-300 hover:scale-110 flex-shrink-0"
                        style={{ 
                          width: '140px',
                          animationDelay: `${index * 30}ms`,
                          animation: 'fadeInUp 0.5s ease-out forwards',
                          opacity: 0
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <div className="relative aspect-square  overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                          <OptimizedImage
                            src={color.image} 
                            alt={color.name}
                            objectFit="cover"
                            className="w-full h-full"
                            sizes="140px"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-earth-brown/90 via-earth-brown/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center p-3">
                            <div className="text-center">
                              <p className="font-angie text-white text-sm font-normal">{color.name}</p>
                              {color.code && (
                                <p className="font-sweet-sans text-white/80 text-xs">{color.code}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="bg-terra-beige/20 text-earth-brown px-6 py-3 rounded-b-2xl text-center">
                <p className="font-sweet-sans text-sm font-semibold">
                  {colorsSectionContent?.slider_footer_text || "Meer dan 50 kleuren beschikbaar"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* B2B Banner */}
        <div className="mt-32 bg-gradient-to-br from-terra-beige/10 via-earth-brown/5 to-terra-beige/10 border-2 border-terra-beige/20  p-12 text-center">
          <h3 className="font-sweet-sans text-3xl sm:text-4xl font-normal text-earth-brown mb-6 uppercase tracking-wide">
            {segmentContent?.b2b_banner_title || "Bent u professional, reseller of groothandel?"}
          </h3>
          <p className="font-sweet-sans text-sm text-almost-black/80 mb-3 leading-relaxed">
            Krijg toegang tot onze B2B portal met exclusieve kortingen
          </p>
          <p className="font-sweet-sans text-xs text-earth-brown font-semibold mb-4">
            {segmentContent?.b2b_banner_features || "✓ Klant-specifieke kortingen • ✓ Op factuur betalen • ✓ Minimum order €250"}
          </p>
          <a 
            href="/shop?search=stalen" 
            className="inline-block mb-6 px-6 py-2 bg-earth-brown text-white font-sweet-sans text-sm uppercase tracking-wider hover:bg-earth-brown/90 transition-colors duration-200"
          >
            Bestel Stalen - €2,50
          </a>
          <div className="block">
            <button
              onClick={scrollToContact}
              className="text-earth-brown font-sweet-sans text-sm uppercase tracking-wider underline hover:text-earth-brown/70 transition-colors duration-200 bg-transparent border-0 cursor-pointer p-0 font-bold"
            >
              Neem contact met ons op!
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
